import {Injectable} from '@angular/core';
import {StudentRecurringAbsences} from '@model/student-recurring-absences';
import {select, Store} from '@ngrx/store';
import {
  getAttendanceByClassValidationAndStatisticsByClass,
  getAttendanceByClassValidations,
  getClassesRelatedToConnectedStaffMember,
  getClassesWithClassLinks,
  getHorizonStudent,
  getHorizonStudents,
  getJustificationsToValidate,
  getJustificationsToValidateCount,
  getAbsenceJustificationProposalsArray,
  getAbsenceJustificationProposalsTotalElements,
  getAbsenceJustificationProposalsToProcessCount,
  getNineUnjustifiedHalfDaysCount,
  getLegalStudentAbsenceByStudentIdByPeriodNoon,
  getManualHorizonStudentSynchronization,
  getStudentAbsencesConfirmation,
  getStudentAttendanceExemptions,
  getStudentAttendances,
  getStudentDifficulties,
  getStudentDisciplinaryReports,
  getStudentOptionChoiceByGroup,
  getStudentOptionChoices,
  getStudentOrientationDecisionByClasss,
  getStudentsInClassDifficulties,
  getStudentsLegalAbsences,
  getStudentsRemarks,
  getStudentStatuses,
  getStudentUnjustifiedLegalAbsencesFromPreviousSchools,
  getUnjustifiedLegalAbsences,
  isUploadingPersonalDocument,
  StoreState,
  getStudentRemarksView,
  getClassesAndVerticalClassesAccessibleToConnectedStaffMember
} from '@redux/reducers';
import {
  JustificationToValidateRef,
  updateJustificationStatusData,
  LegalAbsenceJustification,
  SearchDataLegalAbsenceJustification,
  LegalAbsenceJustificationStatus
} from '@model/legal-absence-justification';
import {
  legalAbsenceJustificationsLoaded,
  legalAbsenceJustificationsToValidateCountLoaded
} from '@redux/actions/legal-absence-justification.action';
import {
  SearchDataStudentAbsenceJustificationProposal,
  StudentAbsenceJustificationProposal, StudentAbsenceJustificationProposalStatus
} from '@model/student-absence-justification-proposal';
import {
  absenceJustificationProposalsLoaded,
  absenceJustificationProposalsReset,
  absenceJustificationProposalsToProcessCountLoaded
} from '@redux/actions/absence-justification-proposal.action';
import {LegalAbsencesEditionPopupData} from '@shared/legal-absences-edition-popup/legal-absences-edition-popup-data';
import {buildLegalAbsencePopupDataFromProposal} from './proposed-justifications/proposal-to-legal-absence';
import {
  buildValidationSummaryMessage,
  validateJustificationsSequentially
} from './justifications-to-validate/justification-validation';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {StudentService} from '@services/student.service';
import {combineLatest, EMPTY, forkJoin, from, mergeMap, Observable, of, onErrorResumeNextWith, OperatorFunction} from 'rxjs';
import {School} from '@model/school';
import {catchError, filter, map, switchMap, take, tap} from 'rxjs/operators';
import {StudentForClassBulkUpdate} from '@model/student-for-class-bulk-update';
import {NINE_UNJUSTIFIED_HALF_DAYS, UnjustifiedAbsencesCriteria} from '@model/legal-absences';
import {
  StudentAttributionDeleted,
  StudentAttributionsReset,
  StudentClassExclusionDeleted,
  StudentClassExclusionSaved,
  StudentClassExclusionsLoaded,
  StudentClassUpdated,
  StudentDifficultiesLoaded,
  StudentDifficultyDeleted,
  StudentDifficultySaved,
  StudentDisciplinaryReportDeleted,
  StudentDisciplinaryReportSaved,
  StudentDisciplinaryReportsLoaded,
  StudentEarlyDepartureDeleted,
  StudentEarlyDepartureReset,
  StudentEarlyDepartureSaved,
  StudentEarlyDeparturesLoaded,
  StudentEndOfYearDecisionByClassLoaded,
  StudentLateArrivalDeleted,
  StudentLateArrivalReset,
  StudentLateArrivalSaved,
  StudentLateArrivalsLoaded,
  StudentLoadedAction,
  StudentOptionChoiceByGroupDeleted,
  StudentOptionChoiceByGroupLoaded,
  StudentOptionChoiceByGroupSaved,
  StudentOptionChoiceDeleted,
  StudentOptionChoiceSaved,
  StudentOptionChoicesLoaded,
  StudentOrientationDecisionByClassLoaded,
  StudentOrientationDecisionByClassReset,
  StudentOrientationDecisionByClassSaved,
  StudentPersonalDocumentUploaded,
  StudentPersonalDocumentUploading,
  StudentRemarksLoaded,
  StudentRemarksViewLoaded,
  StudentRemarksViewReset,
  StudentSearchResultLoadedAction,
  StudentsInClassDifficultiesLoaded,
  StudentStatusLoaded,
  StudentStatusSaved
} from '@redux/actions/students.action';
import {ClassRelation} from '@model/class-relation';
import {Page} from '@model/page';
import {StudentSearchData} from '@model/student-search-data';
import {StudentView} from '@model/student-view';
import {StudentRemark} from '@model/student-remark';
import {StaffMember} from '@model/staffMember';
import {Profile} from '@model/profile';
import {DateTime} from 'luxon';
import {
  legalStudentAbsenceDeleted,
  legalStudentAbsenceParentContacted,
  legalStudentAbsenceReset,
  legalStudentAbsencesLoaded
} from '@redux/actions/legal-student-absence.action';
import {
  JustificationTypeUtils,
  LegalAbsenceSearchCriteria,
  LegalStudentAbsence,
  LegalStudentAbsencePeriod,
  NoonPeriod,
  StudentLegalAbsence
} from '@model/legal-student-absence';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {StudentLateArrival} from '@model/student-late-arrival';
import {StudentEarlyDeparture} from '@model/student-early-departure';
import {StudentClassExclusion} from '@model/student-class-exclusion';
import {StudentInAttribution} from '@model/student-in-attribution';
import {studentsByAttributionAction} from '@redux/actions/attributions.action';
import {AttributionTimeEntry} from '@model/attribution-time-entry';
import {Schedule} from '@model/schedule';
import {StudentDifficulty} from '@model/student-difficulty';
import {Student} from '@model/student';
import {saveAs} from 'file-saver';
import {SchoolReportSnapshots} from '@model/school-reports-snapshots';
import {DisciplinaryReport} from '@model/disciplinary-report';
import {OptionChoice} from '@model/option-choice ';
import {Option} from '@model/option';
import {
  HolidayLoadedAction,
  HolidayResetAction,
  SchoolSectionOptionAddPlaceTakenAction,
  SchoolSectionOptionMinusPlaceTakenAction
} from '@redux/actions/school.action';
import {EvaluationView} from '@model/evaluation';
import {SchoolDiaryEntryEvent} from '@model/teacher-planning-event';
import {YearlyClassInformation} from '@model/yearly-class-information';
import {StudentOptionChoiceByGroupView} from '@model/option-choice-overview';
import {Section} from '@model/section';
import {Class, ClassWithHorizonClassLink} from '@model/class';
import {OrientationsDecision} from '@model/orientations-decision';
import {EndOfYearDecision} from '@model/end-of-year-decision';
import {documentGenerationRequestStarted, documentGenerationRequestStopped} from '@redux/actions/ui.action';
import {PedagogicalFollowUpComment, PedagogicalFollowUpCommentsGroupByCat} from '@model/pedagogical-follow-up-comment';
import {StudentAttribution} from '@model/student-attribution';
import {AttributionService} from '@services/attribution.service';
import {Holiday} from '@model/holiday';
import {SchoolService} from '@services/school.service';
import {
  studentAbsencesReset,
  studentAbsenceVerificationConfirmationLoaded,
  studentsAbsenceVerificationConfirmationReset
} from '@redux/actions/student-absence.action';
import {AbsenceVerificationConfirmation} from '@model/absences-verification';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {StudentDetail, StudentParentRelation} from '@model/student-detail';
import {PedagogicalFollowUpCategory} from '@model/pedagogical-follow-up-category';
import {findClassRelationForYear, findYearOfStudyCodeForYear} from '@shared/util/student-util';
import {getDateTimeFirstDayOfSchoolYearByYear, getDateTimeLastDayOfSchoolYearByYear} from '@shared/util/date-util';
import {PredefinedTextContentMap} from '@model/predefined-text-content';
import {StudentDetention} from '@model/student-detention';
import {
  nineUnjustifiedHalfDaysCountLoaded,
  studentUnjustifiedLegalAbsencesFromPreviousSchoolsLoaded,
  studentUnjustifiedLegalAbsencesFromPreviousSchoolsReset,
  unjustifiedLegalAbsencesLoaded
} from '@redux/actions/unjustified-legal-absence.action';
import {LegalAbsences} from '@model/legal-absences';
import {SchoolSettingKey} from '@model/school-setting-key';
import {StudentStatusByYear} from '@model/student-status';
import {ScheduledDetention} from '@model/scheduled-detention';
import {DetentionRegistration} from '@model/detention-registration';
import {DetentionService} from '@services/detention.service';
import {SearchData} from '@model/search-data';
import {AttributionSummary} from '@model/attribution-summary';
import {CommunicationService} from '@services/communication.service';
import {HorizonStudentService} from '@services/horizon-student.service';
import {
  horizonStudentLoaded,
  horizonStudentReset,
  horizonStudentsLoaded,
  horizonStudentsReset,
  manualHorizonStudentSynchronizationLoaded
} from '@redux/actions/horizon-student.action';
import {HorizonStudent} from '@model/horizon-student';
import {ManualHorizonStudentSynchronization} from '@model/manual-horizon-student-synchronization';
import {HorizonSynchronizationService} from '@services/horizon-synchronization.service';
import {BusinessWarning, captureBusinessWarningOperator} from '@model/business-warning';
import {StudentUnjustifiedLegalAbsencesFromPreviousSchools} from '@model/student-unjustified-legal-absences-from-previous-schools';
import {
  attendanceByClassValidationsLoaded,
  attendanceByClassValidationsReset
} from '@redux/actions/attendance-by-class-validation.action';
import {AttendanceByClassValidation} from '@model/attendance-by-class-validation';
import {AttendanceByClass} from '@model/attendance-by-class';
import {
  attendanceByClassStatisticsLoaded,
  attendanceByClassStatisticsReset
} from '@redux/actions/attendance-by-class-statistic.action';
import {StudentAttendanceExemptionsActions} from '@redux/actions/student-attendance-exemptions.action';
import {StudentAttendanceExemption} from '@model/student-attendance-exemption';
import {StudentAttendanceExemptionBulk} from '@model/student-attendance-exemption-bulk';
import {StudentAttendancesActions} from '@redux/actions/student-attendances.action';
import {StudentAttendance} from '@model/student-attendance';
import {ErrorHandlerService} from '@shared/error-handling/error-handler.service';
import {StorageService} from '@services/storage.service';
import {ClassAndVerticalClass} from '@model/class-and-vertical-class';

@Injectable()
export class StudentsManagementFacadeService {

  profile$: Observable<Profile>;
  school$: Observable<School>;
  staffMembers$: Observable<StaffMember[]>;
  studentsForClassBulkUpdate$: Observable<StudentForClassBulkUpdate[]>;
  studentSearchResult$: Observable<Page<StudentView>>;
  studentsRemarks$: Observable<Page<StudentRemark>>;
  studentRemarksView$: Observable<StudentRemark[]>;
  legalStudentAbsencesByStudentIdByPeriodNoon$: Observable<{[studentId: number]: { [ noonPeriod: string]: LegalStudentAbsence[]}}>;
  studentLegalAbsences$: Observable<LegalStudentAbsence[]>;
  studentLateArrivals$: Observable<StudentLateArrival[]>;
  studentEarlyDepartures$: Observable<StudentEarlyDeparture[]>;
  studentClassExclusions$: Observable<StudentClassExclusion[]>;
  attributionsActivesForTeacher$: Observable<AttributionSummary[]>;
  studentsByAttribution$: Observable<{ [attributionId: number]: StudentInAttribution[] }>;
  connectedTeacherSchedule$: Observable<{ [day: string]: { [time: string]: AttributionTimeEntry } }>;
  schedule$: Observable<Schedule>;
  studentDifficulties$: Observable<StudentDifficulty[]>;
  studentsInClassDifficulties$: Observable<{studentId: number, hasDifficulties: boolean}[]>;
  isMobile$: Observable<boolean>;
  isUploadingPersonalDocument$: Observable<boolean>;
  studentSchoolReportSnapshots$: Observable<SchoolReportSnapshots[]>;
  isAdminStaffMember$: Observable<boolean>;
  isEducatorStaffMember$: Observable<boolean>;
  hasDisciplinaryManagementRole$: Observable<boolean>;
  hasStudentManagementRole$: Observable<boolean>;
  studentDisciplinaryReports$: Observable<DisciplinaryReport[]>;
  optionChoices$: Observable<OptionChoice[]>;
  registrationYear$: Observable<number>;
  studentEvaluations$: Observable<EvaluationView[]>;
  planningEventsByHoursByDates$: Observable<{ [hour: string]: { [date: string]: SchoolDiaryEntryEvent[] }; }>;
  classes$: Observable<Class[]>;
  studentOrientationDecisionByClass$: Observable<OrientationsDecision[]>;
  activeSchoolYear$: Observable<number>;
  yearlyClassesInformationsEntities$: Observable<{ [classId: number]: YearlyClassInformation }>;
  studentOptionChoiceByGroup$: Observable<StudentOptionChoiceByGroupView[]>;
  sections$: Observable<Section[]>;
  schoolSectionOptions$: Observable<Option[]>;
  studentEndOfYearDecisions$: Observable<EndOfYearDecision[]>;
  documentGenerationRequestOngoing$: Observable<boolean>;
  pedagogicalFollowUpComments$: Observable<PedagogicalFollowUpComment[]>;
  pedagogicalFollowUpCommentsGroupByCat$: Observable<PedagogicalFollowUpCommentsGroupByCat[]>;
  studentAttributions$: Observable<StudentAttribution[]>;
  holidays$: Observable<Holiday[]>;
  absenceVerificationConfirmation$: Observable<AbsenceVerificationConfirmation>;
  studentAbsencesByDayByHour$: Observable<any[]>;
  selectedStudent$: Observable<StudentDetail>;
  pedagogicalFollowUpCategories$: Observable<PedagogicalFollowUpCategory[]>;
  predefinedTextContentsMap$: Observable<PredefinedTextContentMap>;
  studentDetentions$: Observable<StudentDetention[]>;
  unjustifiedLegalAbsences$: Observable<LegalAbsences>;
  justificationsToValidate$: Observable<Page<LegalAbsenceJustification>>;
  proposedJustifications$: Observable<StudentAbsenceJustificationProposal[]>;
  proposedJustificationsTotalElements$: Observable<number>;
  justificationsToValidateCount$: Observable<number>;
  proposedJustificationsToProcessCount$: Observable<number>;
  nineUnjustifiedHalfDaysCount$: Observable<number>;
  nineUnjustifiedHalfDaysCriteria$: Observable<UnjustifiedAbsencesCriteria>;
  isPendingRequest$: Observable<boolean>;
  isAbsenceForDayOffEnabled$: Observable<boolean>;
  isAbsenceJustificationValidationEnabled$: Observable<boolean>;
  studentStatuses$: Observable<StudentStatusByYear[]>;
  scheduledDetentions$: Observable<ScheduledDetention[]>;
  detentionRegistrations$: Observable<DetentionRegistration[]>;
  manualHorizonStudentSynchronization$: Observable<Page<ManualHorizonStudentSynchronization>>;
  horizonStudent$: Observable<HorizonStudent>;
  horizonStudents$: Observable<HorizonStudent[]>;
  studentHorizonSynchronizationEnabled$: Observable<boolean>;
  studentHorizonImportCompleted$: Observable<boolean>;
  maxWrittenReasonJustificationForLegalAbsence$: Observable<number>;
  classesRelatedToConnectedStaffMember$ : Observable<Class[]>;
  studentsWithLegalAbsences$: Observable<{student: StudentView, amLegalAbsence: LegalStudentAbsence, pmLegalAbsence: LegalStudentAbsence}[]>;
  studentUnjustifiedLegalAbsencesFromPreviousSchools$: Observable<StudentUnjustifiedLegalAbsencesFromPreviousSchools>;
  attendanceByClassValidations$: Observable<AttendanceByClassValidation[]>;
  elementarySchool$: Observable<boolean>;
  attendanceByClassValidationsAndStatistics$: Observable<AttendanceByClass[]>;
  studentAttendanceExemptions$: Observable<StudentAttendanceExemption[]>;
  studentAttendances$: Observable<StudentAttendance[]>;
  classesWithHorizonClassLinks$: Observable<ClassWithHorizonClassLink[]>;
  yearOfStudyCodeForSelectedStudent$: Observable<string>;
  holidaysForSelectedStudent$: Observable<Holiday[]>;
  isCabangaLite$: Observable<boolean>;
  classesAndVerticalClassesAccessibleToConnectedStaffMember$: Observable<ClassAndVerticalClass[]>;
  isAdminOrEducator$: Observable<boolean>;

  constructor(
              private _location: Location,
              private _router: Router,
              private _store: Store<StoreState>,
              private _localStorage: StorageService,
              private _sharedFacade: SharedFacadeService,
              private _studentService: StudentService,
              private _attributionService: AttributionService,
              private _schoolService: SchoolService,
              private _detentionService: DetentionService,
              private _communicationService: CommunicationService,
              private _horizonStudentService: HorizonStudentService,
              private _horizonSynchronizationService: HorizonSynchronizationService,
              private _snackBar: MatSnackBar,
              private _dialog: MatDialog,
              private _errorHandler: ErrorHandlerService) {
    this.profile$ = this._sharedFacade.profile$;
    this.school$ = this._sharedFacade.school$;
    this.elementarySchool$ = this.school$.pipe(map(school => school.elementarySchool ?? false));
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
    this.studentsForClassBulkUpdate$ = combineLatest([this.school$, this.studentSearchResult$]).pipe(
      map(([school, students]) => students.content.map(student => {
        const currentClassRelation = findClassRelationForYear(student.classRelations, school.activeSchoolYear);
        const nextClassRelation = findClassRelationForYear(student.classRelations, Number(school.activeSchoolYear) + 1);
        return {
          studentId: student.id,
          studentFirstName: student.firstName,
          studentLastName: student.lastName,
          currentClassId: currentClassRelation && currentClassRelation.classId,
          currentClassName: currentClassRelation && currentClassRelation.className,
          nextClassId: nextClassRelation && nextClassRelation.classId,
          nextClassName: nextClassRelation && nextClassRelation.className
        } as StudentForClassBulkUpdate;
      }))
    );
    this.studentsRemarks$ = this._store.pipe(
      select(getStudentsRemarks)
    );
    this.studentRemarksView$ = this._store.pipe(
      select(getStudentRemarksView)
    );
    this.legalStudentAbsencesByStudentIdByPeriodNoon$ = this._store.pipe(
      select(getLegalStudentAbsenceByStudentIdByPeriodNoon)
    );
    this.studentLegalAbsences$ = this._sharedFacade.studentLegalAbsences$;
    this.studentLateArrivals$ = this._sharedFacade.studentLateArrivals$;
    this.studentEarlyDepartures$ = this._sharedFacade.studentEarlyDepartures$;
    this.studentClassExclusions$ = this._sharedFacade.studentClassExclusions$;
    this.attributionsActivesForTeacher$ = this._sharedFacade.attributionsActivesForTeacher$;
    this.studentsByAttribution$ = this._sharedFacade.studentsByAttribution$;
    this.connectedTeacherSchedule$ = this._sharedFacade.connectedTeacherSchedule$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.studentDifficulties$ = this._store.pipe(
      select(getStudentDifficulties)
    );
    this.studentsInClassDifficulties$ = this._store.pipe(
      select(getStudentsInClassDifficulties)
    );
    this.isUploadingPersonalDocument$ = this._store.pipe(
      select(isUploadingPersonalDocument)
    );
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.studentSchoolReportSnapshots$ = this._sharedFacade.studentSchoolReportSnapshots$;
    this.isAdminStaffMember$ = this._sharedFacade.isAdminStaffMember$;
    this.isEducatorStaffMember$ = this._sharedFacade.isEducatorStaffMember$;
    this.hasDisciplinaryManagementRole$ = this._sharedFacade.hasDisciplinaryManagementRole$;
    this.hasStudentManagementRole$ = this._sharedFacade.hasStudentManagementRole$;
    this.studentDisciplinaryReports$ = this._store.pipe(
      select(getStudentDisciplinaryReports)
    );
    this.optionChoices$ = this._store.pipe(
      select(getStudentOptionChoices)
    );
    this.registrationYear$ = this._sharedFacade.registrationYear$;
    this.studentEvaluations$ = this._sharedFacade.studentEvaluations$;
    this.planningEventsByHoursByDates$ = this._sharedFacade.planningEventsByHoursByDates$;
    this.classes$ = this._sharedFacade.classes$;
    this.studentOrientationDecisionByClass$ = this._store.pipe(
      select(getStudentOrientationDecisionByClasss)
    );
    this.activeSchoolYear$ =  this._sharedFacade.activeSchoolYear$;
    this.yearlyClassesInformationsEntities$ = this._sharedFacade.yearlyClassesInformationsEntities$;
    this.studentOptionChoiceByGroup$ = this._store.pipe(
      select(getStudentOptionChoiceByGroup)
    );
    this.sections$ = this._sharedFacade.sections$;
    this.schoolSectionOptions$ = this._sharedFacade.schoolSectionOptions$;
    this.studentEndOfYearDecisions$ = this._sharedFacade.studentEndOfYearDecisions$;
    this.documentGenerationRequestOngoing$ = this._sharedFacade.documentGenerationRequestOngoing$;
    this.pedagogicalFollowUpComments$ = this._sharedFacade.pedagogicalFollowUpComments$;
    this.pedagogicalFollowUpCommentsGroupByCat$ = this._sharedFacade.pedagogicalFollowUpCommentsGroupByCat$;
    this.studentAttributions$ = this._sharedFacade.studentAttributions$;
    this.holidays$ = this._sharedFacade.holidays$;
    this.absenceVerificationConfirmation$ = this._store.pipe(
      select(getStudentAbsencesConfirmation)
    );
    this.studentAbsencesByDayByHour$ = this._sharedFacade.studentAbsencesByDayByHour$;
    this.selectedStudent$ = this._sharedFacade.selectedStudent$;
    this.pedagogicalFollowUpCategories$ = this._sharedFacade.pedagogicalFollowUpCategories$;
    this.predefinedTextContentsMap$ = this._sharedFacade.predefinedTextContentsMap$;
    this.studentDetentions$ = this._sharedFacade.studentDetentions$;
    this.unjustifiedLegalAbsences$ = this._store.pipe(
      select(getUnjustifiedLegalAbsences)
    );
    this.justificationsToValidate$ = this._store.pipe(
      select(getJustificationsToValidate)
    );
    this.proposedJustifications$ = this._store.pipe(
      select(getAbsenceJustificationProposalsArray)
    );
    this.proposedJustificationsTotalElements$ = this._store.pipe(
      select(getAbsenceJustificationProposalsTotalElements)
    );
    this.justificationsToValidateCount$ = this._store.select(getJustificationsToValidateCount);
    this.proposedJustificationsToProcessCount$ = this._store.select(getAbsenceJustificationProposalsToProcessCount);
    this.nineUnjustifiedHalfDaysCount$ = this._store.select(getNineUnjustifiedHalfDaysCount);
    this.nineUnjustifiedHalfDaysCriteria$ = this.school$.pipe(map(school => this._nineUnjustifiedHalfDaysCriteria(school)));
    this.studentUnjustifiedLegalAbsencesFromPreviousSchools$ = this._store.pipe(
      select(getStudentUnjustifiedLegalAbsencesFromPreviousSchools)
    )
    this.isPendingRequest$ = this._sharedFacade.isPendingRequest$;
    this.isAbsenceForDayOffEnabled$ = this._sharedFacade.schoolSettings$.pipe(
      map(schoolSettings => schoolSettings.find(schoolSetting => schoolSetting.key === SchoolSettingKey.ABSENCE_FOR_DAY_OFF)?.enabled)
    );
    this.isAbsenceJustificationValidationEnabled$ = this._sharedFacade.schoolSettings$.pipe(
      map(schoolSettings => schoolSettings.find(schoolSetting => schoolSetting.key === SchoolSettingKey.ABSENCE_JUSTIFICATION_VALIDATION_ENABLED)?.enabled === true)
    );
    this.maxWrittenReasonJustificationForLegalAbsence$ = this._sharedFacade.schoolSettings$.pipe(
      map(schoolSettings => schoolSettings.find(schoolSetting => schoolSetting.key === SchoolSettingKey.MAX_WRITTEN_REASON_JUSTIFICATION_FOR_LEGAL_ABSENCE)?.numericValue)
    );
    this.studentStatuses$ = this._store.pipe(
      select(getStudentStatuses)
    );
    this.detentionRegistrations$ = this._sharedFacade.detentionRegistrations$;
    this.scheduledDetentions$ = this._sharedFacade.scheduledDetentions$;
    this.manualHorizonStudentSynchronization$ = this._store.select(getManualHorizonStudentSynchronization);
    this.horizonStudent$ = this._store.select(getHorizonStudent);
    this.horizonStudents$ = this._store.select(getHorizonStudents);

    this.studentHorizonSynchronizationEnabled$ = this._sharedFacade.studentHorizonSynchronizationEnabled$;
    this.studentHorizonImportCompleted$ = this.school$.pipe(
      map(school => school.studentHorizonImportCompleted ?? false)
    );

    this.classesRelatedToConnectedStaffMember$  = this._store.select(getClassesRelatedToConnectedStaffMember);
    this.studentsWithLegalAbsences$ = this._store.select(getStudentsLegalAbsences);
    this.attendanceByClassValidations$ = this._store.select(getAttendanceByClassValidations);
    this.attendanceByClassValidationsAndStatistics$ = this._store.select(getAttendanceByClassValidationAndStatisticsByClass);
    this.studentAttendanceExemptions$ = this._store.select(getStudentAttendanceExemptions);
    this.studentAttendances$ = this._store.select(getStudentAttendances);
    this.classesWithHorizonClassLinks$ = this._store.pipe(select(getClassesWithClassLinks)) as Observable<ClassWithHorizonClassLink[]>;
    this.yearOfStudyCodeForSelectedStudent$ = combineLatest([
      this.selectedStudent$,
      this.activeSchoolYear$,
      this.classesWithHorizonClassLinks$
    ]).pipe(
      map(([student, activeSchoolYear, classes]) => findYearOfStudyCodeForYear(student, activeSchoolYear, classes))
    );
    this.holidaysForSelectedStudent$ = this._sharedFacade.holidaysForSelectedStudent$;
    this.isCabangaLite$ = this._sharedFacade.isCabangaLite$;
    this.classesAndVerticalClassesAccessibleToConnectedStaffMember$ = this._store.pipe(select(getClassesAndVerticalClassesAccessibleToConnectedStaffMember)) as Observable<ClassAndVerticalClass[]>;
    this.isAdminOrEducator$ = combineLatest([this.isAdminStaffMember$, this.isEducatorStaffMember$]).pipe(
      map(([isAdmin, isEducator]) => isAdmin || isEducator)
    );
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  searchStudents(searchData: StudentSearchData): void {
    this._sharedFacade.searchStudents(searchData);
  }

  loadStudentsByClass(classId: number, classPresenceDate?: DateTime) {
    this._sharedFacade.loadStudentsByClass(classId, classPresenceDate);
  }

  loadStudentsByClasses(classIds: number[], classPresenceDate?: DateTime) {
    if (!classIds || classIds.length === 0) {
      return;
    }
    this.school$.pipe(
      take(1),
      switchMap(school => forkJoin(classIds.map(classId => this._studentService.searchStudents(school.id, {
        pageNumber: 0,
        pageSize: 500,
        classId: classId,
        classPresenceDate: classPresenceDate
      }))))
    ).subscribe(pages => {
      const mergedPage: Page<StudentView> = {
        content: pages.flatMap(page => page.content ?? []),
        totalElements: pages.reduce((total, page) => total + (page.totalElements ?? 0), 0)
      };
      this._store.dispatch(new StudentSearchResultLoadedAction(mergedPage));
    });
  }

  loadStudentBySectionForClassUpdate(sectionName: string) {
    const searchParams: StudentSearchData = {
      sectionName: sectionName,
      pageSize: 2000
    };
    this.searchStudents(searchParams);
  }

  updateStudentClassRequest(studentId: number, classRelation: ClassRelation): Observable<void> {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.updateStudentClass(school.id, studentId, classRelation))
    );
  }

  updateStudentClass(studentId: number, classRelation: ClassRelation) {
    this.updateStudentClassRequest(studentId, classRelation).subscribe(() => {
      this.selectStudent(studentId);
      this._store.dispatch(new StudentClassUpdated(studentId, classRelation));
      this._snackBar.open('Classe enregistrée', null, {duration: 2000});
    });
  }

  resetStudentSearchResult() {
    this._sharedFacade.resetStudentSearchResult();
  }

  saveStudentRemarkAndReload(studentRemark: StudentRemark, emailToParent: boolean, emailToEducator: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveRemark(school.id, studentRemark, emailToParent, emailToEducator))
    ).subscribe(_ => this.loadStudentsRemarks());
  }

  loadStudentsRemarks(searchData?: SearchData) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudentsRemarks(school.id, school.activeSchoolYear, searchData))
    ).subscribe(remarks => this._store.dispatch(new StudentRemarksLoaded(remarks)));
  }

  loadStudentRemarksView(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.listStudentsRemarksForStudent(school.id, studentId, school.activeSchoolYear))
    ).subscribe(remarks => this._store.dispatch(new StudentRemarksViewLoaded(remarks)));
  }

  resetStudentRemarksView(){
    this._store.dispatch(new StudentRemarksViewReset());
  }

  loadStudentRemarksForStudent(studentId: number, schoolYear?: number) {
    this._sharedFacade.loadStudentRemarksForStudent(studentId, schoolYear);
  }

  deleteStudentRemarkAndReload(studentRemark: StudentRemark) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteStudentRemark(school.id, studentRemark.studentId, studentRemark.id))
    ).subscribe(() => this.loadStudentsRemarks());
  }

  resetStudentRemarks() {
    this._sharedFacade.resetStudentRemarks();
  }

  loadStudentLegalAbsences(searchCriteria: LegalAbsenceSearchCriteria) {
    const {classIds, ...criteriaWithoutClassIds} = searchCriteria;
    if (classIds && classIds.length === 0) {
      return;
    }
    if (classIds && classIds.length > 0) {
      this.school$.pipe(
        take(1),
        switchMap(school => forkJoin(classIds.map(classId =>
          this._studentService.getStudentLegalAbsences(school.id, {...criteriaWithoutClassIds, classId: classId}))))
      ).subscribe(absencesByClass => this._store.dispatch(legalStudentAbsencesLoaded({absences: absencesByClass.flat()})));
      return;
    }
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudentLegalAbsences(school.id, criteriaWithoutClassIds))
    ).subscribe(absences => this._store.dispatch(legalStudentAbsencesLoaded({absences: absences})));
  }

  saveNumberOfUnjustifiedLegalAbsencesFromPreviousSchools(studentId: number, numberOfUnjustifiedLegalAbsences: number): void {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._studentService.saveNumberOfUnjustifiedLegalAbsencesFromPreviousSchools(
          school.id,
          studentId,
          school.activeSchoolYear,
          numberOfUnjustifiedLegalAbsences
        ).pipe(
          map(() => school)
        )
      )
    ).subscribe(school => {
      this._store.dispatch(studentUnjustifiedLegalAbsencesFromPreviousSchoolsLoaded({
        studentUnjustifiedLegalAbsencesFromPreviousSchools: {
          schoolId: school.id,
          studentId,
          schoolYear: school.activeSchoolYear,
          numberOfUnjustifiedLegalAbsences
        }
      }));
    });
  }

  loadStudentUnjustifiedLegalAbsencesFromPreviousSchoolsIfAllowed(studentId: number): void {
    this._store.dispatch(studentUnjustifiedLegalAbsencesFromPreviousSchoolsReset());
    combineLatest([this.isAdminStaffMember$, this.isEducatorStaffMember$]).pipe(
      take(1),
      filter(([isAdmin, isEducator]) => isAdmin || isEducator),
      switchMap(() => this.school$),
      take(1),
      switchMap(school =>
        this._studentService.findStudentUnjustifiedLegalAbsencesFromPreviousSchools(
          school.id,
          studentId,
          school.activeSchoolYear
        ))
    ).subscribe(unjustifiedAbsencesFromPreviousSchools  => {
      this._store.dispatch(studentUnjustifiedLegalAbsencesFromPreviousSchoolsLoaded({
        studentUnjustifiedLegalAbsencesFromPreviousSchools: unjustifiedAbsencesFromPreviousSchools
      }));
    });
  }

  deleteLegalStudentAbsence(studentId: number, date: DateTime, noonPeriod: NoonPeriod) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteLegalStudentAbsence(school.id, studentId, date, noonPeriod))
    ).subscribe(() => this._store.dispatch(legalStudentAbsenceDeleted({
      studentId: studentId,
      date: date,
      noonPeriod: noonPeriod
    })));
  }

  downloadLegalAbsenceDocument(studentId: number, justificationId: number, documentId: string) {
    this._sharedFacade.downloadLegalAbsenceDocument(studentId, justificationId, documentId);
  }

  deleteLegalAbsenceDocument(studentId: number, justificationId: number, searchCriteria?: LegalAbsenceSearchCriteria) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteLegalAbsenceDocument(school.id, studentId, justificationId))
    ).subscribe(() => {
      this._snackBar.open('Document supprimé', null, {duration: 2000});
      if (searchCriteria) {
        this.loadStudentLegalAbsences(searchCriteria);
      } else {
        this.loadStudentLegalAbsencesForStudent(studentId);
      }
    }
    );
  }

  uploadLegalAbsenceDocument(studentId: number, justificationId: number, file: File, searchCriteria?: LegalAbsenceSearchCriteria) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.putLegalAbsenceDocument(school.id, studentId, justificationId, file))
    ).subscribe(() => {
      this._snackBar.open('Document téléchargé', null, {duration: 2000});
      if (searchCriteria) {
        this.loadStudentLegalAbsences(searchCriteria);
      } else {
        this.loadStudentLegalAbsencesForStudent(studentId);
      }
    });
  }

  sendCommunicationForLegalAbsenceToParent(messages: {studentId: number, content: string}[], legalAbsenceDate: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        const observables = messages.map(m =>
          this._communicationService.sendNotificationToParent(school.id, m.studentId, m.content).pipe(
            tap(resp => Object.keys(resp).filter(key => resp[key] > 0).forEach((key: 'SMS' | 'EMAIL') => this.createParentNotificationsInformation(m.studentId, legalAbsenceDate, key)))
          )
        );
        return forkJoin(observables);
      }),
    ).subscribe({
      next: (resp) => {
        const messageMap = resp.reduce((acc, val) => {
          acc.set('SMS', acc.get('SMS') + val['SMS']);
          acc.set('EMAIL', acc.get('EMAIL') + val['EMAIL']);
          return acc;
        }, new Map<string, number>([['SMS', 0], ['EMAIL', 0]]));
        const message = this._buildMessage(messageMap);
        this._snackBar.open(message, 'Fermer', {duration: 2000});
      },
      error: () => this._snackBar.open('Erreur lors de l\'envoi de la communication', 'Fermer', {duration: 2000}),
    });
  }

  private _buildMessage(map: Map<string, number>): string {
    const smsSent = map.get('SMS') || 0;
    const emailSent = map.get('EMAIL') || 0;

    const messages = {
      '0-0': 'Pas de communication activée pour les parents',
      '0-1': `${emailSent} email${emailSent > 1 ? 's' : ''} envoyé${emailSent > 1 ? 's' : ''}`,
      '1-0': `${smsSent} SMS envoyé${smsSent > 1 ? 's' : ''}`,
      '1-1': `${smsSent} SMS envoyé${smsSent > 1 ? 's' : ''} - ${emailSent} email${emailSent > 1 ? 's' : ''} envoyé${emailSent > 1 ? 's' : ''}`
    };

    const key = `${smsSent > 0 ? 1 : 0}-${emailSent > 0 ? 1 : 0}`;
    return messages[key] || '';
  }

  saveStudentRecurringAbsences(studentId: number, absences: StudentRecurringAbsences): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.createRecurringLegalAbsences(school.id, studentId, absences)),
      captureBusinessWarningOperator<BusinessWarning>(this._snackBar)
    ).subscribe(response => {
      if (!response.warnings?.length) {
        this._snackBar.open('Absences récurrentes enregistrées.', undefined, {duration: 2000});
      }
      this.loadStudentLegalAbsencesForStudent(studentId);
    });
  }

  saveLegalStudentAbsencePeriod(studentId: number, legalStudentAbsencePeriod: LegalStudentAbsencePeriod, searchCriteria?: LegalAbsenceSearchCriteria) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveLegalAbsenceStudentPeriod(school.id, studentId, legalStudentAbsencePeriod)),
      captureBusinessWarningOperator<void>(this._snackBar)
    ).subscribe(() => {
      if (searchCriteria) {
        this.loadStudentLegalAbsences(searchCriteria);
      } else {
        this.loadStudentLegalAbsencesForStudent(studentId);
      }
    });
  }

  saveLegalStudentsAbsencesPeriods(studentLegalAbsences: StudentLegalAbsence[], searchCriteria?: LegalAbsenceSearchCriteria): void {
    if (studentLegalAbsences.length === 0) {
      return;
    }

    let savedLegalAbsenceCount = 0;
    let elementarySchool = false;
    this.school$.pipe(
      take(1),
      tap(school => elementarySchool = school.elementarySchool ?? false),
      switchMap(school =>
          from(studentLegalAbsences).pipe(
            mergeMap(studentLegalAbsence =>
                this._studentService.saveLegalAbsenceStudentPeriod(school.id, studentLegalAbsence.studentId, studentLegalAbsence.legalStudentAbsencePeriod)
                  .pipe(
                    tap({
                      error: error => this._errorHandler.handleError(error)
                    }),
                    onErrorResumeNextWith(EMPTY)
                  ),
              2
            )
          )
        )
      ).subscribe({
      next: () => savedLegalAbsenceCount++,
      complete: () => {
        this.loadStudentLegalAbsences(searchCriteria);
        const justificationType = studentLegalAbsences[0].legalStudentAbsencePeriod.justificationType;
        const justificationLabel = JustificationTypeUtils.asText(justificationType, elementarySchool);
        this._snackBar.open(`${savedLegalAbsenceCount}/${studentLegalAbsences.length} absence(s) encodée(s) : ${justificationType} - ${justificationLabel}.`, null, {duration: 2000});
      }
    });
  }

  extendLegalStudentAbsencePeriod(studentId: number, date: DateTime, noonPeriod: NoonPeriod, justificationId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.updateLegalStudentAbsence(school.id, studentId, date, noonPeriod, justificationId, true))
    ).subscribe(() => {
      this.loadStudentLegalAbsencesForStudent(studentId);
    });
  }

  loadStudentLegalAbsencesForStudent(studentId: number) {
    this._sharedFacade.loadStudentLegalAbsencesForStudent(studentId);
  }

  loadStudentLateArrivals() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsLateArrivals(school.id, school.activeSchoolYear))
    ).subscribe(items => this._store.dispatch(new StudentLateArrivalsLoaded(items)));
  }

  saveStudentLateArrival(studentLateArrival: StudentLateArrival, emailToParents : boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveStudentLateArrivals(school.id, studentLateArrival, emailToParents))
    ).subscribe(id => this._store.dispatch(
      new StudentLateArrivalSaved(Object.assign({}, studentLateArrival, {id: id})))
    );
  }

  deleteStudentLateArrival(studentLateArrival: StudentLateArrival) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteStudentLateArrival(school.id, studentLateArrival))
    ).subscribe(() => this._store.dispatch(
      new StudentLateArrivalDeleted(studentLateArrival.id))
    );
  }

  loadStudentEarlyDepartures() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsEarlyDepartures(school.id, school.activeSchoolYear))
    ).subscribe(items => this._store.dispatch(new StudentEarlyDeparturesLoaded(items)));
  }

  saveStudentEarlyDeparture(studentEarlyDeparture: StudentEarlyDeparture) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveStudentEarLyDeparture(school.id, studentEarlyDeparture))
    ).subscribe(id => this._store.dispatch(
      new StudentEarlyDepartureSaved(Object.assign({}, studentEarlyDeparture, {id: id})))
    );
  }

  deleteStudentEarlyDeparture(studentEarlyDeparture: StudentEarlyDeparture) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteEarlyDeparture(school.id, studentEarlyDeparture))
    ).subscribe(() => this._store.dispatch(
      new StudentEarlyDepartureDeleted(studentEarlyDeparture.id))
    );
  }

  loadStudentLateArrivalsForStudent(studentId: number) {
    this._sharedFacade.loadStudentLateArrivalsForStudent(studentId);
  }

  loadStudentEarlyDeparturesForStudent(studentId: number) {
    this._sharedFacade.loadStudentEarlyDeparturesForStudent(studentId);
  }

  loadStudentClassExclusionsForStudent(studentId: number, schoolYear?: number) {
    this._sharedFacade.loadStudentClassExclusionsForStudent(studentId, schoolYear);
  }

  loadStudentClassExclusions() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsClassExclusions(school.id, school.activeSchoolYear))
    ).subscribe(items => this._store.dispatch(new StudentClassExclusionsLoaded(items)));
  }

  saveStudentClassExclusion(studentClassExclusion: StudentClassExclusion) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveStudentClassExclusion(school.id, studentClassExclusion))
    ).subscribe(id => this._store.dispatch(
      new StudentClassExclusionSaved(Object.assign({}, studentClassExclusion, {id: id})))
    );
  }

  deleteStudentClassExclusion(classExclusion: StudentClassExclusion) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteClassExclusion(school.id, classExclusion))
    ).subscribe(() => this._store.dispatch(
      new StudentClassExclusionDeleted(classExclusion.id))
    );
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadAttributionsActivesForConnectedTeacher() {
    this._sharedFacade.loadAttributionsActivesForConnectedTeacher();
  }

  loadStudentsForAttribution(attributionId: number) {
    this._sharedFacade.loadStudentsByAttribution(attributionId);
  }

  resetStudentsInAttributions() {
    this._store.dispatch(studentsByAttributionAction.resetStudentsByAttribution());
  }

  loadConnectedTeacherSchedule() {
    this._sharedFacade.loadConnectedTeacherSchedule();
  }

  loadStudentDifficulties() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsDifficultiesForConnectedTeacher(school.id))
    ).subscribe(difficulties => this._store.dispatch(new StudentDifficultiesLoaded(difficulties)));
  }

  saveStudentDifficulty(difficulty: StudentDifficulty) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveStudentDifficulty(school.id, difficulty))
    ).subscribe(id => this._store.dispatch(
      new StudentDifficultySaved(Object.assign({}, difficulty, {id: id})))
    );
  }

  deleteStudentDifficulty(difficulty: StudentDifficulty) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteDifficulty(school.id, difficulty))
    ).subscribe(() => this._store.dispatch(
      new StudentDifficultyDeleted(difficulty.id))
    );
  }

  loadStudentDifficultiesForStudent(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadDifficultiesForStudent(school.id, studentId, school.activeSchoolYear))
    ).subscribe(difficulties => this._store.dispatch(new StudentDifficultiesLoaded(difficulties)));
  }

  loadStudentsInClassDifficulties(classId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentsDifficultiesStatus(school.id, classId))
    ).subscribe(studentsInClassDifficulties => this._store.dispatch(new StudentsInClassDifficultiesLoaded(studentsInClassDifficulties)));
  }

  uploadPersonalDocument(studentId: number, uploadedFile: File) {
    this._store.dispatch(new StudentPersonalDocumentUploading());
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.uploadPersonalDocument(school.id, studentId, uploadedFile))
    ).subscribe(() => {
      this._store.dispatch(new StudentPersonalDocumentUploaded());
      this.selectStudent(studentId);
    }, () => this._store.dispatch(new StudentPersonalDocumentUploaded()));
  }

  deletePersonalDocument(studentId: number, fileId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deletePersonalDocument(school.id, studentId, fileId))
    ).subscribe(() => this.selectStudent(studentId));
  }

  downloadPersonalDocument(studentId: number, file: {documentId: string; documentName: string; }) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getPersonalDocument(school.id, studentId, file.documentId))
    ).subscribe(_file => {
      saveAs(_file, file.documentName);
    });
  }

  updatePersonalDocumentConfidentiality(document: { documentId: string; confidential: boolean }, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._studentService.updatePersonalDocumentConfidentiality(school.id, studentId, document.documentId, document.confidential))
    ).subscribe(() => this.selectStudent(studentId));
  }

  selectStudent(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudent(school.id, studentId))
    ).subscribe(student => this._store.dispatch(new StudentLoadedAction(student)));
  }

  loadStudentSchoolReportSnapshots(studentId: number) {
    this._sharedFacade.loadStudentSchoolReportSnapshots(studentId);
  }

  resetStudentReports() {
    this._sharedFacade.resetStudentReports();
  }

  createOrUpdateStudent(student: Student): Observable<Student> {
    if (student.id) {
      return this.school$.pipe(
        take(1),
        switchMap(school => this._studentService.updateStudent(school.id, student)),
        map(() => student)
      )
    } else {
      return this.school$.pipe(
        take(1),
        switchMap(school => this._studentService.createStudent(school.id, student))
      )
    }
  }

  saveStudent(student: Student) {
    this.createOrUpdateStudent(student).subscribe((_student) => this.selectStudent(_student.id));
  }

  downloadStudentSchoolReportSnapshot(studentReport: SchoolReportSnapshots) {
    this._sharedFacade.downloadStudentSchoolReportSnapshot(studentReport);
  }

  loadDisciplinaryReportsForStudent(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadDisciplinaryReportsForStudent(school.id, studentId))
    ).subscribe(reports => this._store.dispatch(new StudentDisciplinaryReportsLoaded(reports)));
  }

  loadStudentEvaluationsForStudent(studentId: number) {
    this._sharedFacade.loadStudentEvaluationsForStudent(studentId);
  }

  saveStudentDisciplinaryReport(disciplinaryReport: DisciplinaryReport) {
    if (disciplinaryReport.id) {
      this.school$.pipe(
        take(1),
        switchMap(school => this._studentService.updateDisciplinaryReport(school.id, disciplinaryReport))
      ).subscribe(() => this._store.dispatch(new StudentDisciplinaryReportSaved(disciplinaryReport)));
    } else {
      this.school$.pipe(
        take(1),
        switchMap(school => this._studentService.saveDisciplinaryReport(school.id, disciplinaryReport)),
        switchMap(disciplinaryReportId => this.profile$.pipe(
          take(1),
          map(profile => ({id: disciplinaryReportId, staffMemberLastName: profile.lastName, staffMemberFirstName: profile.firstName}))
        ))
      ).subscribe(_disciplinaryReport => this._store.dispatch(new StudentDisciplinaryReportSaved({
        ...disciplinaryReport,
        ..._disciplinaryReport
        })));
    }
  }

  deleteStudentDisciplinaryReport(studentId: number, disciplinaryReportId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteDisciplinaryReport(school.id, studentId, disciplinaryReportId))
    ).subscribe(() => this._store.dispatch(new StudentDisciplinaryReportDeleted(disciplinaryReportId)));
  }

  loadOptionsForStudent(studentId: number) {
    combineLatest([this.school$, this.registrationYear$]).pipe(
      take(1),
      switchMap(([school, registrationYear]) => this._studentService.loadOptionsForStudent(school.id, studentId, registrationYear))
    ).subscribe(options => this._store.dispatch(new StudentOptionChoicesLoaded(options)));
  }

  addOptionForStudent(studentId: number, option: Option) {
    const studentOption = { optionId: option.id, optionName: option.name, sectionName: option.sectionName, studentId, year: option.schoolYear};
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.addOptionForStudent(school.id, studentId, option.id))
    ).subscribe(() => {
      this._store.dispatch(new StudentOptionChoiceSaved(studentOption));
      this._store.dispatch(new SchoolSectionOptionAddPlaceTakenAction(option.id));
    });
  }

  removeOptionForStudent(studentId: number, optionId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.removeOptionForStudent(school.id, studentId, optionId))
    ).subscribe(() => {
      this._store.dispatch(new StudentOptionChoiceDeleted(optionId));
      this._store.dispatch(new SchoolSectionOptionMinusPlaceTakenAction(optionId));
    });
  }

  addOptionForStudentInClass(studentId: number, option: {id: number, name: string}, optionsSectionName: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.addOptionForStudent(school.id, studentId, option.id))
    ).subscribe(() => {
      this._store.dispatch(new StudentOptionChoiceByGroupSaved(studentId, option, optionsSectionName));
      this._store.dispatch(new SchoolSectionOptionAddPlaceTakenAction(option.id));
    });
  }

  removeOptionForStudentInClass(studentId: number, optionId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.removeOptionForStudent(school.id, studentId, optionId))
    ).subscribe(() => {
      this._store.dispatch(new StudentOptionChoiceByGroupDeleted(studentId, optionId));
      this._store.dispatch(new SchoolSectionOptionMinusPlaceTakenAction(optionId));
    });
  }

  loadSchoolDiary(startDate: DateTime, endDate: DateTime, studentId: number) {
    this._sharedFacade.loadSchoolDiaryForStudent(studentId, startDate, endDate);
  }

  loadStudentOrientationDecisionByClass(classId: number, year: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentOrientationDecisionByClass(school.id, classId, year))
    ).subscribe(studentOrientationDecisionByClass => {
      this._store.dispatch(new StudentOrientationDecisionByClassLoaded(studentOrientationDecisionByClass));
    });
  }

  resetStudentOrientationDecisionByClass() {
    this._store.dispatch(new StudentOrientationDecisionByClassReset());
  }

  saveStudentOrientationDecision(studentId: number, year: number, studentOrientationDecisionPartial: OrientationsDecision) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveStudentOrientationDecisionByClass(school.id, studentId, year, studentOrientationDecisionPartial))
    ).subscribe(_ => {
      this._store.dispatch(new StudentOrientationDecisionByClassSaved(studentId, studentOrientationDecisionPartial));
      this._snackBar.open('Décision enregistrée', null, {duration: 2000});
    });
  }

  updateRegistrationYear(number: any) {
    this._sharedFacade.updateRegistrationYear(number);
  }

  loadRegistrationYear() {
    this._sharedFacade.loadRegistrationYear();
  }

  loadStudentOptionChoiceByClassView(classId: number) {
    combineLatest([this.school$, this.registrationYear$]).pipe(
      take(1),
      switchMap(([school, registrationYear])  => this._studentService.getStudentOptionChoiceByClassView(school.id, registrationYear, classId))
    ).subscribe(studentOptionByClass => {
      this._store.dispatch(new StudentOptionChoiceByGroupLoaded(studentOptionByClass));
    });
  }

  loadStudentOptionChoiceBySectionView(sectionName: string) {
    combineLatest([this.school$, this.activeSchoolYear$]).pipe(
      take(1),
      switchMap(([school, activeSchoolYear])  =>
        this._studentService.getStudentOptionChoiceBySectionView(school.id, parseInt(`${activeSchoolYear}`, 10) + 1, sectionName))
    ).subscribe(studentOptionByClass => {
      this._store.dispatch(new StudentOptionChoiceByGroupLoaded(studentOptionByClass));
    });
  }

  loadSectionOptions(sectionName: string) {
    this._sharedFacade.loadSectionOptions(sectionName);
  }

  loadStudentEndOfYearDecisionByClass(classId: number, year?: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentEndOfYearDecisionByClass(school.id, classId, year || school.activeSchoolYear))
    ).subscribe(studentEndOfYearDecisionByClass => {
      this._store.dispatch(new StudentEndOfYearDecisionByClassLoaded(studentEndOfYearDecisionByClass));
    });
  }

  resetStudentEndOfYearDecisions() {
    this._sharedFacade.resetStudentEndOfYearDecisions();
  }

  saveStudentEndOfYearDecision(studentId: number, studentEndOfYearDecisionPartial: EndOfYearDecision, year?: number) {
    this._sharedFacade.saveStudentEndOfYearDecision(studentId, studentEndOfYearDecisionPartial, year);
  }

  downloadEndOfYearDecisionsPDF(classId: number, typeOfDoc: string, year?: number) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => {
        if (typeOfDoc === 'DELIBERATION') {
          return this._studentService.createEndOfYearDecisionsReportGenerationRequest(school.id, classId, year || school.activeSchoolYear);
        } else {
          return this._studentService.createEndOfYearDecisionsByStudentsReportGenerationRequest(school.id, classId, year || school.activeSchoolYear);
        }
      })
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  loadYearlyClassesInformations() {
    this._sharedFacade.loadYearlyClassesInformations();
  }

  sendStudentPhoto(studentId: number, uploadedFile: File) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.uploadStudentPhoto(school.id, studentId, uploadedFile))
    ).subscribe(() => {
      this._snackBar.open('Photo enregistrée', null, {duration: 2000});
    }, err => {
      if (err.status === 409 && err.error.detail?.includes('Format type')) {
        this._snackBar.open('Format de la photo est invalide', null, {duration: 2000});
      } else  {
        throw err;
      }
    });
  }

  deleteStudentPhoto(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.removeStudentPhoto(school.id, studentId))
    ).subscribe(() => {
      this._snackBar.open('Photo supprimée', null, {duration: 2000});
    });
  }

  loadPedagogicalFollowUpComments(studentId: number) {
    this._sharedFacade.loadPedagogicalFollowUpComments(studentId);
  }

  loadPedagogicalFollowUpCategories() {
    this._sharedFacade.loadPedagogicalFollowUpCategories();
  }

  resetPedagogicalFollowUpComments() {
    this._sharedFacade.resetPedagogicalFollowUpComments();
  }

  loadStudentAttributions(studentId: number) {
    this._sharedFacade.loadStudentAttributions(studentId);
  }

  removeStudentToAttributionFromStudentAttributions(attributionId: number, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.removeStudentToAttribution(school.id, attributionId, studentId))
    ).subscribe(() => this._store.dispatch(new StudentAttributionDeleted(attributionId)));
  }

  resetStudentAttributions() {
    this._store.dispatch(new StudentAttributionsReset());
  }

  uploadSielFile(uploadedFile: File) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.uploadSielFile(school.id, uploadedFile))
    ).subscribe(() => {
      this._snackBar.open('Le fichier a bien été envoyé, vous recevrez un mail pour vous informer si tout s\'est bien déroulé', null, {duration: 5000});
    }, err => {
      if (err.status === 400 && err.error.code?.includes('BAD_CSV_CONTENT')) {
        this._snackBar.open(err.error.detail, null, {duration: 5000});
      } else  {
        throw err;
      }
    });
  }

  loadHolidaysCurrentActiveSchoolYear() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getHolidays(school.id, school.activeSchoolYear))
    ).subscribe((holidays) => this._store.dispatch(new HolidayLoadedAction(holidays)));
  }

  resetHolidays() {
    this._store.dispatch(new HolidayResetAction());
  }

  loadAbsencesVerification(date: DateTime, hour: string) {
    combineLatest([this.school$, this.profile$]).pipe(
      take(1),
      switchMap(([school, profile]) => this._studentService.loadStudentsAbsencesVerificationsConfirmations(school.id, profile.id, date, hour))
    ).subscribe(absenceVerificationConfirmation => {
      this._store.dispatch(studentAbsenceVerificationConfirmationLoaded({ absenceVerificationConfirmation }));
    });
  }

  confirmNoAbsences(date: DateTime, hour: string, skipSendBackend: boolean) {
    combineLatest([this.school$, this.profile$]).pipe(
      take(1),
      switchMap(([school, profile]) => {
        if (skipSendBackend) {
          return of({date, hour, staffMemberId: profile.id});
        }
        return this._studentService.updateStudentsAbsencesVerificationsConfirmations(school.id, {date, hour, staffMemberId: profile.id});
      })
    ).subscribe((absenceVerificationConfirmation) => {
      this._store.dispatch(studentAbsenceVerificationConfirmationLoaded({ absenceVerificationConfirmation }));
    });
  }

  resetAbsencesVerification() {
    this._store.dispatch(studentsAbsenceVerificationConfirmationReset());
  }

  goBack(defaultRoute: string) {
    const state = this._location.getState() as {navigationId: number}
    if (state.navigationId > 1) {
      this._location.back();
    } else {
      this._router.navigate([defaultRoute]);
    }
  }

  loadStudentAbsencesForStudent(studentId: number) {
    this._sharedFacade.loadStudentAbsencesForStudent(studentId);
  }

  resetSelectedStudent() {
    this._sharedFacade.resetSelectedStudent();
  }

  savePedagogicalFollowUpComment(ilpComment: PedagogicalFollowUpComment) {
    this._sharedFacade.savePedagogicalFollowUpComment(ilpComment);
  }

  deletePedagogicalFollowUpComment(studentId: number, commentId: number) {
    this._sharedFacade.deletePedagogicalFollowUpComment(studentId, commentId);
  }

  loadUnjustifiedAbsences(minNbrOfAbsences: number, startDate: DateTime, endDate: DateTime, educatorId?: number, includePreviousSchoolAbsences?: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getUnjustifiedAbsences(school.id, startDate, endDate, minNbrOfAbsences, educatorId, includePreviousSchoolAbsences))
    ).subscribe(unjustifiedLegalAbsences => {
      this._store.dispatch(unjustifiedLegalAbsencesLoaded({unjustifiedLegalAbsences}));
    });
  }

  loadJustificationsWaitingValidation(searchData: SearchDataLegalAbsenceJustification) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getLegalAbsenceJustifications(school.id, {...searchData, status: LegalAbsenceJustificationStatus.WAITING_FOR_VALIDATION}))
    ).subscribe(justifications => {
      this._store.dispatch(legalAbsenceJustificationsLoaded({justifications}));
    });
  }

  updateJustificationStatus(items: JustificationToValidateRef[], updateStatusData: updateJustificationStatusData , searchData: SearchDataLegalAbsenceJustification) {
    this.school$.pipe(
      take(1),
      switchMap(school => validateJustificationsSequentially(items,
        item => this._studentService.patchLegalAbsenceJustificationValidation(school.id, item.studentId, item.justificationId, updateStatusData)))
    ).subscribe(summary => {
      const isValidateAction = updateStatusData.status === LegalAbsenceJustificationStatus.ACCEPTED || (updateStatusData.type !== null && updateStatusData.type !== undefined)  ? true : false;
      this._snackBar.open(buildValidationSummaryMessage(summary, isValidateAction), null, {duration: 3000});
      this.loadJustificationsWaitingValidation(searchData);
    });
  }

  loadProposedJustifications(searchData: SearchDataStudentAbsenceJustificationProposal) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentAbsenceJustificationProposals(school.id, searchData))
    ).subscribe(page => {
      this._store.dispatch(absenceJustificationProposalsLoaded({page}));
    });
  }

  resetProposedJustifications() {
    this._store.dispatch(absenceJustificationProposalsReset());
  }

  downloadProposedJustificationDocument(proposal: StudentAbsenceJustificationProposal) {
    this._sharedFacade.downloadProposedJustificationDocument(proposal);
  }

  loadJustificationsToValidateCount() {
    combineLatest([this.school$, this.isAbsenceJustificationValidationEnabled$]).pipe(
      take(1),
      switchMap(([school, isValidationEnabled]) => isValidationEnabled
        ? this._schoolService.getLegalAbsenceJustifications(school.id,
          {status: LegalAbsenceJustificationStatus.WAITING_FOR_VALIDATION, withinValidationPeriodOnly: true, page: 0, size: 1}).pipe(map(page => page.totalElements))
        : of(null))
    ).subscribe(count => this._store.dispatch(legalAbsenceJustificationsToValidateCountLoaded({count})));
  }

  loadProposedJustificationsToProcessCount() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentAbsenceJustificationProposals(school.id,
        {statuses: [StudentAbsenceJustificationProposalStatus.TO_PROCESS], page: 0, size: 1, withinModificationPeriodOnly: true })),
      map(page => page.totalElements)
    ).subscribe(count => this._store.dispatch(absenceJustificationProposalsToProcessCountLoaded({count})));
  }

  loadNineUnjustifiedHalfDaysCount() {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        const criteria = this._nineUnjustifiedHalfDaysCriteria(school);
        return this._schoolService.getUnjustifiedAbsences(school.id, criteria.start, criteria.end, criteria.minNbrOfAbsences, null, true);
      }),
      map(absences => absences.studentsLegalAbsenceCountView.length)
    ).subscribe(count => this._store.dispatch(nineUnjustifiedHalfDaysCountLoaded({count})));
  }

  private _nineUnjustifiedHalfDaysCriteria(school: School): UnjustifiedAbsencesCriteria {
    return {
      minNbrOfAbsences: NINE_UNJUSTIFIED_HALF_DAYS,
      start: getDateTimeFirstDayOfSchoolYearByYear(school.activeSchoolYear),
      end: getDateTimeLastDayOfSchoolYearByYear(school.activeSchoolYear)
    };
  }

  proposedJustificationPopupData(proposal: StudentAbsenceJustificationProposal): Observable<LegalAbsencesEditionPopupData> {
    return combineLatest([
      this.isAbsenceForDayOffEnabled$,
      this.elementarySchool$,
      this._yearOfStudyCodeForStudent(proposal.studentId),
      this._sharedFacade.getProposedJustificationDocumentFromProposal(proposal)
    ]).pipe(
      take(1),
      map(([isAbsenceForDayOffEnabled, elementarySchool, yearOfStudyCode, file]) =>
        buildLegalAbsencePopupDataFromProposal(proposal, {
          isAbsenceForDayOffEnabled,
          elementarySchool,
          yearOfStudyCode,
          document: file ? new File([file.content], file.filename, {type: file.content.type}) : null
        }))
    );
  }

  createJustificationFromProposal(proposal: StudentAbsenceJustificationProposal,
                                  legalStudentAbsencePeriod: LegalStudentAbsencePeriod,
                                  onProcessed: () => void) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveLegalAbsenceStudentPeriod(school.id, proposal.studentId, legalStudentAbsencePeriod).pipe(
        captureBusinessWarningOperator<void>(this._snackBar),
        switchMap(() => this._studentService.markAbsenceJustificationProposalProcessed(school.id, proposal.studentId, proposal.id))
      ))
    ).subscribe({
      next: () => {
        this._snackBar.open('Justificatif encodé', null, {duration: 2000});
        onProcessed();
      },
      error: () => this._snackBar.open('Erreur lors de l\'encodage du justificatif', null, {duration: 4000})
    });
  }

  rejectProposedJustification(studentId: number, proposalId: string, reason: string, onRejectedCallback: () => void): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.patchAbsenceJustificationProposal(school.id, studentId, proposalId, {status: StudentAbsenceJustificationProposalStatus.REJECTED, reason}
      ))
    ).subscribe({
      next: () => {
        this._snackBar.open('Proposition de justificatif refusée', null, {duration: 2000});
        onRejectedCallback();
      }
    });
  }

  private _yearOfStudyCodeForStudent(studentId: number): Observable<string> {
    return combineLatest([
      this.school$.pipe(switchMap(school => this._studentService.getStudent(school.id, studentId))),
      this.activeSchoolYear$,
      this.classesWithHorizonClassLinks$
    ]).pipe(
      take(1),
      map(([student, activeSchoolYear, classes]) => findYearOfStudyCodeForYear(student, activeSchoolYear, classes))
    );
  }

  printListingUnjustifiedAbsences(minNbrOfAbsences: number, startDate: DateTime, endDate: DateTime, studentIds: number[], educatorId?: number, includePreviousSchoolAbsences?: boolean){
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.generateUnjustifiedAbsencesListing(school.id, startDate, endDate, minNbrOfAbsences, studentIds, educatorId, includePreviousSchoolAbsences))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  printUnjustifiedAbsences(studentIds: number[], startDate: DateTime, endDate: DateTime) {
    this.printLegalAbsences(studentIds, startDate, endDate, true);
  }

  printAllLegalAbsences(studentId: number) {
    this.school$.pipe(
      take(1)
    ).subscribe(school => {
      const activeSchoolYear = Number(school.activeSchoolYear);
      const startDate = DateTime.local(activeSchoolYear, 8, 20);
      const endDate = DateTime.local(activeSchoolYear + 1, 7, 10);
      this.printLegalAbsences([studentId], startDate, endDate, false);
    });
  }

  private printLegalAbsences(studentIds: number[],  startDate: DateTime, endDate: DateTime, onlyUnjustified: boolean) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.generateUnjustifiedAbsencesReport(school.id, startDate, endDate, studentIds, onlyUnjustified))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  printPartialAttendanceCertificate(studentId: number) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.generatePartialAttendanceCertificate(school.id, studentId))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  loadPredefinedTextContents() {
    this._sharedFacade.loadPredefinedTextContents();
  }

  loadStudentDetentionsForStudent(studentId: number, schoolYear?: number) {
    this._sharedFacade.loadStudentDetentionsByStudent(studentId, schoolYear);
  }

  updateProcessedByDisciplinaryStaffMember(remark: StudentRemark) {
    this.school$.pipe(
        take(1),
        switchMap(school => this._studentService.updateRemarkProcessedByDisciplinaryStaffMember(school.id, remark))
    ).subscribe(
        () => this.loadStudentRemarksForStudent(remark.studentId)
    );
  }

  updateLateArrivalProcessedByDisciplinaryStaffMember(lateArrival: StudentLateArrival) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.updateLateArrivalProcessedByDisciplinaryStaffMember(school.id, lateArrival))
    ).subscribe(
      () => this.loadStudentLateArrivalsForStudent(lateArrival.studentId)
    );
  }

  updateLateArrivalJustified(lateArrival: StudentLateArrival) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.updateLateArrivalJustified(school.id, lateArrival))
    ).subscribe(
      () => this.loadStudentLateArrivalsForStudent(lateArrival.studentId)
    );
  }

  loadSchoolSettings() {
    this._sharedFacade.loadSchoolSettings();
  }

  exportStudentsData() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.exportStudentsData(school.id, school.activeSchoolYear))
    ).subscribe(_file => saveAs(_file.content, _file.filename));
  }

  updateStudentStatus(studentId: number, status: StudentStatusByYear) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.updateStudentStatus(school.id, studentId, status))
    ).subscribe(() => this._store.dispatch(new StudentStatusSaved(status)));
  }

  loadStudentStatuses(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudentStatuses(school.id, studentId))
    ).subscribe(studentStatuses => this._store.dispatch(new StudentStatusLoaded(studentStatuses)));
  }

  saveStudentDetention(studentDetention: StudentDetention) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.saveStudentDetention(school.id, studentDetention)),
    ).subscribe(_studentDetention => this.loadStudentDetentionsForStudent(studentDetention.studentId));
  }

  saveStudentDetentionAndRegistration(studentDetention: StudentDetention, detentionId: string, sendEmails: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.saveStudentDetention(school.id, studentDetention)),
    ).subscribe(_studentDetention => this.saveDetentionRegistration(_studentDetention.id, detentionId, sendEmails, studentDetention.studentId));
  }

  saveDetentionRegistration(studentDetentionId: string, detentionId: string, sendEmails: boolean, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.registerStudentDetention(school.id, studentDetentionId, detentionId, sendEmails))
    ).subscribe(_ => {
      this.loadStudentDetentionsForStudent(studentId);
    });
  }

  deleteStudentDetention(studentId: number, studentDetentionId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.deleteStudentDetention(school.id, studentId, studentDetentionId))
    ).subscribe(_ => {
      this.loadStudentDetentionsForStudent(studentId);
    });
  }

  loadDetentionRegistrations(studentId: number, studentDetentionId: string) {
    this._sharedFacade.loadDetentionRegistrations(studentId, studentDetentionId);
  }

  deleteAndReloadDetentionRegistration(scheduledDetentionId: string, studentDetentionId: string, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._detentionService.deleteDetentionRegistration(school.id, scheduledDetentionId, studentDetentionId))
    ).subscribe(_ => {
      this.loadStudentDetentionsForStudent(studentId);
      this.loadDetentionRegistrations(studentId, studentDetentionId);
    });
  }

  loadScheduledDetentions() {
    this._sharedFacade.loadScheduledDetentions();
  }

  resetDetentionRegistrations() {
    this._sharedFacade.resetDetentionRegistrations();
  }

  printDisciplinaryDetails(studentId: number) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.generateDisciplinaryDetails(school.id, studentId))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  createParentNotificationsInformation(studentId: number, legalStudentAbsenceDate: DateTime, type: 'SMS' | 'EMAIL' | 'PHONE') {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.postParentNotificationsInformation(school.id, studentId, legalStudentAbsenceDate, type))
    ).subscribe(() => {
      this._store.dispatch(legalStudentAbsenceParentContacted({studentId, legalStudentAbsenceDate, typeContact: type}));
    });
  }

  loadManualHorizonStudentSynchronization() {
    this.isAdminStaffMember$.pipe(
      take(1),
      filter(isAdmin => isAdmin),
      switchMap(() => this.school$),
      take(1),
      switchMap(school => this._studentService.getManualHorizonStudentSynchronization(school.id))
    ).subscribe(page => this._store.dispatch(manualHorizonStudentSynchronizationLoaded({manualHorizonStudentSynchronization: page})));
  }

  loadHorizonStudent(horizonStudentId: string) {
    this._store.dispatch(horizonStudentReset());
    this.school$.pipe(
      take(1),
      filter(school => school.studentHorizonSynchronizationEnabled),
      switchMap(school => this._horizonStudentService.getHorizonStudent(school.educationAuthority.fase, horizonStudentId, school.activeSchoolYear)),
      this._catchErrorPermissionOnStudentSignaletic()
    ).subscribe(horizonStudent => this._store.dispatch(horizonStudentLoaded({horizonStudent})));
  }

  synchronizeHorizonStudent(manualHorizonStudentSynchronizationId: string, studentId: number, callback: () => void) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._horizonSynchronizationService.postSynchronizeHorizonStudent(school.id, manualHorizonStudentSynchronizationId, studentId))
    ).subscribe(() => {
      this._snackBar.open('Importation de l\'élève de Horizon vers Cabanga effectuée', null, {duration: 2000});
      this.loadManualHorizonStudentSynchronization();
      callback();
    });
  }

  importHorizonStudentAndRedirectToForm(horizonStudentId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._horizonSynchronizationService.postImportHorizonStudent(school.id, horizonStudentId))
    ).subscribe((s: {id: number}) => {
      this._snackBar.open('Importation de l\'élève de Horizon vers Cabanga effectuée', null, {duration: 2000});
      this._router.navigate(['app/admin/students/list', s.id, 'form-view']);
    });
  }

  searchHorizonStudents(searchData: StudentSearchData): void {
    this.school$.pipe(
      take(1),
      filter(school => school.studentHorizonSynchronizationEnabled),
      switchMap(school => this._horizonStudentService.getHorizonStudents(school.educationAuthority.fase, searchData, school.activeSchoolYear))
    ).subscribe(results => {
      this._store.dispatch(horizonStudentsLoaded({horizonStudents: results}));
    });
  }

  resetHorizonStudents() {
    this._store.dispatch(horizonStudentsReset());
  }

  resetHorizonStudent() {
    this._store.dispatch(horizonStudentReset());
  }

  private _catchErrorPermissionOnStudentSignaletic(): OperatorFunction<any, any> {
    return catchError(error => {
      if (error.status === 403) {
        this._snackBar.open('Vous n\'avez pas les permissions pour accéder à la signalétique élèves.', null, {duration: 2000});
      }
      return of(null);
    });
  }

  deleteRelation(studentId: number, parentRelationId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.deleteRelation(school.id, studentId, parentRelationId))
    ).subscribe(() => {
      this.selectStudent(studentId);
    });
  }

  createParentRelation(studentId: number, parentRelation: StudentParentRelation): Observable<void> {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.postParentsRelations(school.id, studentId, parentRelation))
    );
  }

  createParentRelationAndSelectStudent(studentId: number, parentRelation: StudentParentRelation) {
    this.createParentRelation(studentId, parentRelation).subscribe(() => {
      this.selectStudent(studentId);
    });
  }

  updateParentRelation(studentId: number, parentRelation: StudentParentRelation) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.putParentsRelations(school.id, studentId, parentRelation))
    ).subscribe(() => {
      this.selectStudent(studentId);
    });
  }

  loadClassStaffMemberRelations() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => {
      this._sharedFacade.loadClassStaffMemberRelations(profile.id);
    });
  }

  loadHorizonClassLinks() {
    this._sharedFacade.loadHorizonClassLinks();
  }

  updateAttendanceByClassValidation(classId: number, date: DateTime, period: string, validated: boolean ) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.putAttendanceByClassValidations(school.id, classId, date, period, validated))
    ).subscribe(() => {
      this.loadAttendanceByClassValidations(date);
    });
  }

  loadAttendanceByClassValidations(date: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getAttendanceByClassValidations(school.id, date))
    ).subscribe((page) => {
      this._store.dispatch(attendanceByClassValidationsLoaded({ attendanceByClassValidations: page.content }));
    });
  }

  loadAttendanceByClassStatistics(date: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getAttendanceByClassStatistics(school.id, date))
    ).subscribe((statistics) => {
      this._store.dispatch(attendanceByClassStatisticsLoaded({ attendanceByClassStatistics: statistics }));
    });
  }

  resetAttendanceByClass() {
    this._store.dispatch(attendanceByClassValidationsReset());
    this._store.dispatch(attendanceByClassStatisticsReset());
    this.resetStudentAttendances();
  }

  getSelectedEstablishmentFase(): number {
    const selectedEstablishmentFaseFromLocalStorage = this._localStorage.getItem<string>("cabanga_selected_establishment_fase");
    return selectedEstablishmentFaseFromLocalStorage ? parseInt(selectedEstablishmentFaseFromLocalStorage): null;
  }

  updateSelectedEstablishmentFase(establishmentFase: number) {
    this._localStorage.setItem("cabanga_selected_establishment_fase", establishmentFase);
  }

  loadStudentAttendanceExemptions(students: number | number[]) {
    this.resetStudentAttendanceExemptions();
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentAttendanceExemptions(school.id, students, school.activeSchoolYear))
    ).subscribe((exceptions) => {
      this._store.dispatch(StudentAttendanceExemptionsActions.setStudentAttendanceExemptions({ studentAttendanceExemptions: exceptions }));
    });
  }

  loadStudentsAttendanceExemptions(studentIds: number[], date: DateTime) {
    this.resetStudentAttendanceExemptions();
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentsAttendanceExemptions(school.id, studentIds, date))
    ).subscribe(exemptions => {
      this._store.dispatch(
        StudentAttendanceExemptionsActions.setStudentAttendanceExemptions({
          studentAttendanceExemptions: exemptions
        })
      );
    });
  }

  createStudentAttendanceExemption(exemption: StudentAttendanceExemption) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.postStudentAttendanceExemption(school.id, exemption))
    ).subscribe(() => {
      this.loadStudentAttendanceExemptions(exemption.studentId);
    });
  }

  deleteStudentAttendanceExemption(studentAttendanceExemptionId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.deleteStudentAttendanceExemption(school.id, studentAttendanceExemptionId))
    ).subscribe(() => {
      this._store.dispatch(StudentAttendanceExemptionsActions.deleteStudentAttendanceExemption({ studentAttendanceExemptionId }));
    });
  }

  createBulkStudentAttendanceExemptions(exemptions: StudentAttendanceExemptionBulk, studentsToReload?: number | number[]): void {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._schoolService.postBulkStudentAttendanceExemptions(school.id, exemptions)
      )
    ).subscribe(() => {
      this._snackBar.open('Jours non contrôlés enregistrés.', undefined, {duration: 2000});
      if (studentsToReload !== undefined && studentsToReload !== null) {
        this.loadStudentAttendanceExemptions(studentsToReload);
      }
    });
  }

  removeBulkStudentAttendanceExemptions(exemptions: StudentAttendanceExemptionBulk, studentsToReload?: number | number[]): void {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._schoolService.removeBulkStudentAttendanceExemptions(school.id, exemptions)
      )
    ).subscribe(() => {
      this._snackBar.open('Jours non contrôlés supprimés.', undefined, {duration: 2000});
      if (studentsToReload !== undefined && studentsToReload !== null) {
        this.loadStudentAttendanceExemptions(studentsToReload);
      }
    });
  }

  resetStudentAttendanceExemptions() {
    this._store.dispatch(StudentAttendanceExemptionsActions.resetStudentAttendanceExemptions());
  }

  loadStudentAttendances(fromDate: DateTime, toDate: DateTime, classIds?: number[]) {
    if (classIds && classIds.length === 0) {
      this.resetStudentAttendances();
      return;
    }
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getStudentAttendances(school.id, fromDate, toDate, classIds))
    ).subscribe(studentAttendances => {
      this._store.dispatch(StudentAttendancesActions.setStudentAttendances({studentAttendances}));
    });
  }

  resetStudentAttendances() {
    this._store.dispatch(StudentAttendancesActions.resetStudentAttendances());
  }

  resetLegalStudentAbsence() {
    this._store.dispatch(legalStudentAbsenceReset());
  }

  resetStudentEarlyDepartures() {
    this._store.dispatch(new StudentEarlyDepartureReset());
  }

  resetStudentLateArrivals() {
    this._store.dispatch(new StudentLateArrivalReset());
  }

  resetStudentAbsences() {
    this._store.dispatch(studentAbsencesReset());
  }
}
